import { useState } from 'react';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@jeraisonne/ui';
import type { ClassAssignmentSummary } from '@/lib/student-objectives/objective-types';
import { ExerciseAccessBadge } from './exercise-access-badge';

interface Props {
  summary: ClassAssignmentSummary;
  onStartExercise: (exerciseId: string) => void;
  /** Exercice en cours d'ouverture (spinner sur son bouton). */
  pendingExerciseId?: string | null;
}

export function ClassCard({ summary, onStartExercise, pendingExerciseId = null }: Props) {
  const [expanded, setExpanded] = useState(false);

  const activeItems = summary.assignments.filter((a) => a.status !== 'completed');

  return (
    <Card className="border-ink-100">
      <CardHeader className="pb-2 pt-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <CardTitle className="font-display text-ink-900 truncate text-lg font-bold">
              📚 {summary.className}
            </CardTitle>
            <p className="text-ink-500 mt-0.5 truncate text-sm">{summary.tutorName}</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {summary.pendingCount > 0 && (
              <span className="rounded-full border border-amber-200 bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                {summary.pendingCount} à faire
              </span>
            )}
            {summary.completedCount > 0 && (
              <span className="rounded-full border border-emerald-200 bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">
                {summary.completedCount} ✓
              </span>
            )}
          </div>
        </div>
      </CardHeader>

      {activeItems.length > 0 && (
        <CardContent className="pb-3">
          <button
            type="button"
            className="text-primary-600 mb-3 flex items-center gap-1 text-xs font-medium"
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? '▲' : '▼'} {activeItems.length} exercice{activeItems.length > 1 ? 's' : ''}{' '}
            assigné{activeItems.length > 1 ? 's' : ''}
          </button>
          {expanded && (
            <div className="space-y-2">
              {activeItems.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between gap-2 rounded-lg border border-emerald-100 bg-white p-3"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-ink-900 truncate text-sm font-medium">
                      {item.exerciseTitle}
                    </p>
                    {item.dueAt && (
                      <p className="text-ink-400 mt-0.5 text-xs">
                        Avant le {new Date(item.dueAt).toLocaleDateString('fr-FR')}
                      </p>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <ExerciseAccessBadge
                      accessState={{ allowed: true, reason: 'class_assignment' }}
                    />
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => onStartExercise(item.exerciseId)}
                      loading={pendingExerciseId === item.exerciseId}
                      disabled={pendingExerciseId !== null}
                    >
                      {pendingExerciseId === item.exerciseId ? 'Ouverture…' : 'Lancer'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
